import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ServiceHighlights = () => {
  const services = [
    {
      id: 1,
      icon: 'Droplets',
      title: 'Wash & Fold / Iron',
      description: 'Professional washing with premium detergents and fabric softeners',
      features: ['Eco-friendly detergents', 'Fabric care', 'Quick turnaround'],
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
      image: '/assets/images/Wash & Fold.jpg'
    },  
    {
      id: 2,
      icon: 'Zap',
      title: 'Steam Press',
      description: 'Professional steam pressing for perfect creases and wrinkle-free garments',
      features: ['Steam pressing', 'Crease perfection', 'Garment care'],
      color: 'bg-amber-500',
      bgColor: 'bg-amber-50',
      iconColor: 'text-amber-600',
      image: '/assets/images/steam_press.jpg'
    },
    {
      id: 3,
      icon: 'Sparkles',
      title: 'Dry Clean',
      description: 'Specialized cleaning for delicate fabrics and formal wear',
      features: ['Delicate handling', 'Stain removal', 'Fabric protection'],
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600',
      image: '/assets/images/Dry Clean.jpg'
    },
    {
      id: 4,
      icon: 'Truck',
      title: 'Free Delivery',
      description: 'Convenient pickup and delivery service at your doorstep',
      features: ['Same day service', 'Flexible timing', 'Safe handling'],
      color: 'bg-green-500',
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600',
      image: '/assets/images/Free Delivery.jpg'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Our Premium Services
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Experience the finest laundry care with our comprehensive range of professional services
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {services?.map((service) => (
            <motion.div
              key={service?.id}
              variants={itemVariants}
              whileHover={{ 
                y: -8,
                transition: { duration: 0.3, ease: "easeOut" }
              }}
              className="group relative"
            >
              <div className="bg-card rounded-2xl overflow-hidden shadow-elevation-1 hover:shadow-elevation-3 transition-all duration-300 border border-border h-full group">
                {/* Service Image */}
                <div className="relative h-48 overflow-hidden">
                  <Image
                    src={service?.image}
                    alt={service?.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                  
                  {/* Icon Overlay */}
                  <div className="absolute top-4 right-4">
                    <div className={`w-12 h-12 ${service?.bgColor} rounded-lg flex items-center justify-center backdrop-blur-sm bg-white/90`}>
                      <Icon 
                        name={service?.icon} 
                        size={20} 
                        className={service?.iconColor}
                      />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4">
                  <h3 className="text-xl font-semibold text-foreground group-hover:text-primary transition-colors duration-300">
                    {service?.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {service?.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-2">
                    {service?.features?.map((feature, index) => (
                      <li key={index} className="flex items-center space-x-2 text-sm text-muted-foreground">
                        <Icon name="Check" size={14} className="text-success flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-8 border border-border">
            <h3 className="text-2xl font-semibold text-foreground mb-4">
              Ready to Experience Premium Care?
            </h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Join thousands of satisfied customers who trust us with their laundry needs
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.location.href = '/services-overview'}
              className="bg-primary text-primary-foreground px-8 py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors duration-300 shadow-elevation-2"
            >
              View All Services
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServiceHighlights;